import React, { useState } from 'react';
import { availableFacilities } from '../constants/facilities';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { useToast } from '../hooks/use-toast';
import { Plus, Edit, Trash2, DollarSign, Eye, Upload, Package } from 'lucide-react';

const Packages = () => {
  const [packages, setPackages] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [previewPackage, setPreviewPackage] = useState(null);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    title: '',
    price: '',
    description: '',
    duration: '',
    image: '',
    facilities: []
  });

  const resetForm = () => {
    setFormData({ title: '', price: '', description: '', duration: '', image: '', facilities: [] });
    setEditingPackage(null);
  };

  const handleEdit = (pkg) => {
    setFormData({
      title: pkg.title,
      price: pkg.price.toString(),
      description: pkg.description,
      duration: pkg.duration,
      image: pkg.image,
      facilities: pkg.facilities
    });
    setEditingPackage(pkg);
    setIsDialogOpen(true);
  };

  const handleDelete = (packageId) => {
    setPackages(packages.filter(pkg => pkg.id !== packageId));
    toast({ title: 'Package deleted', description: 'The package has been successfully deleted.' });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const packageData = {
      id: editingPackage ? editingPackage.id : Date.now().toString(),
      title: formData.title,
      price: parseFloat(formData.price),
      description: formData.description,
      duration: formData.duration,
      image: formData.image,
      facilities: formData.facilities
    };

    if (editingPackage) {
      setPackages(packages.map(pkg => pkg.id === editingPackage.id ? packageData : pkg));
      toast({ title: 'Package updated', description: 'The package has been successfully updated.' });
    } else {
      setPackages([...packages, packageData]);
      toast({ title: 'Package added', description: 'The new package has been successfully added.' });
    }

    setIsDialogOpen(false);
    resetForm();
  };

  const handleFacilityChange = (facility, checked) => {
    setFormData(prev => ({
      ...prev,
      facilities: checked
        ? [...prev.facilities, facility]
        : prev.facilities.filter(f => f !== facility)
    }));
  };

  const handlePreview = () => {
    setPreviewPackage({
      title: formData.title,
      price: parseFloat(formData.price) || 0,
      description: formData.description,
      duration: formData.duration,
      image: formData.image,
      facilities: formData.facilities
    });
    setIsPreviewOpen(true);
  };

  const PackageCard = ({ pkg }) => (
    <Card className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 transition-all duration-200 transform hover:scale-[1.02]">
      {pkg.image ? (
        <div className="relative">
          <img src={pkg.image} alt={pkg.title} className="w-full h-48 object-cover rounded-t-lg" />
          <Badge className="absolute top-2 right-2 bg-blue-600 text-white">{pkg.duration}</Badge>
        </div>
      ) : (
        <div className="relative h-48 bg-slate-700 rounded-t-lg flex items-center justify-center">
          <Package className="h-12 w-12 text-slate-500" />
          <Badge className="absolute top-2 right-2 bg-blue-600 text-white">{pkg.duration}</Badge>
        </div>
      )}
      <CardContent className="p-6">
        <div className="space-y-4">
          <div>
            <h3 className="text-xl font-bold text-white">{pkg.title}</h3>
            <div className="flex items-center space-x-1 text-green-400 mt-1">
              <DollarSign className="h-4 w-4" />
              <span className="text-2xl font-bold">${pkg.price}</span>
            </div>
          </div>
          <p className="text-slate-300 text-sm line-clamp-3">{pkg.description}</p>
          <div className="flex flex-wrap gap-1">
            {pkg.facilities.slice(0, 4).map((f) => (
              <Badge key={f} variant="secondary" className="text-xs bg-slate-700 text-slate-300">{f}</Badge>
            ))}
            {pkg.facilities.length > 4 && (
              <Badge variant="secondary" className="text-xs bg-slate-700 text-slate-300">+{pkg.facilities.length - 4} more</Badge>
            )}
          </div>
          <div className="flex space-x-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => handleEdit(pkg)} className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-700">
              <Edit className="h-4 w-4 mr-1" />Edit
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleDelete(pkg.id)} className="border-red-600 text-red-400 hover:bg-red-600/20">
              <Trash2 className="h-4 w-4 mr-1" />Delete
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Package Management</h1>
          <p className="text-slate-400 mt-1">Manage your hotel packages and special offers</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />Add Package
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl bg-slate-800 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle>{editingPackage ? 'Edit Package' : 'Add New Package'}</DialogTitle>
              <DialogDescription className="text-slate-400">
                {editingPackage ? 'Update package details' : 'Create a new package for your hotel'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Package Title</Label>
                  <Input id="title" value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} className="bg-slate-700 border-slate-600" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price">Price ($)</Label>
                  <Input id="price" type="number" value={formData.price} onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))} className="bg-slate-700 border-slate-600" required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="duration">Duration</Label>
                <Input id="duration" value={formData.duration} onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))} placeholder="e.g., 2 Days 1 Night" className="bg-slate-700 border-slate-600" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea id="description" value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} className="bg-slate-700 border-slate-600" rows={3} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="image">Image URL</Label>
                <div className="flex space-x-2">
                  <Input id="image" value={formData.image} onChange={(e) => setFormData(prev => ({ ...prev, image: e.target.value }))} placeholder="https://example.com/image.jpg" className="bg-slate-700 border-slate-600" />
                  <Button type="button" variant="outline" className="border-slate-600"><Upload className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className="space-y-3">
                <Label>Included Facilities</Label>
                <div className="grid grid-cols-3 gap-3 max-h-32 overflow-y-auto">
                  {availableFacilities.map((facility) => (
                    <div key={facility} className="flex items-center space-x-2">
                      <Checkbox id={`pkg-${facility}`} checked={formData.facilities.includes(facility)} onCheckedChange={(checked) => handleFacilityChange(facility, checked)} />
                      <Label htmlFor={`pkg-${facility}`} className="text-sm">{facility}</Label>
                    </div>
                  ))}
                </div>
              </div>
              <DialogFooter className="space-x-2">
                <Button type="button" variant="outline" onClick={handlePreview} className="border-slate-600">
                  <Eye className="h-4 w-4 mr-2" />Preview
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  {editingPackage ? 'Update Package' : 'Add Package'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {packages.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map((pkg) => <PackageCard key={pkg.id} pkg={pkg} />)}
        </div>
      ) : (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-12 text-center">
            <Package className="h-16 w-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">No Packages Found</h3>
            <p className="text-slate-400 mb-4">No packages have been created yet.</p>
            <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />Add First Package
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Package Preview</DialogTitle>
            <DialogDescription className="text-slate-400">How your package will appear to customers</DialogDescription>
          </DialogHeader>
          {previewPackage && (
            <div className="space-y-4">
              {previewPackage.image ? (
                <div className="relative">
                  <img src={previewPackage.image} alt={previewPackage.title} className="w-full h-40 object-cover rounded-lg" />
                  <Badge className="absolute top-2 right-2 bg-blue-600 text-white">{previewPackage.duration}</Badge>
                </div>
              ) : (
                <div className="relative h-40 bg-slate-700 rounded-lg flex items-center justify-center">
                  <Package className="h-10 w-10 text-slate-500" />
                  <Badge className="absolute top-2 right-2 bg-blue-600 text-white">{previewPackage.duration}</Badge>
                </div>
              )}
              <div>
                <h3 className="text-lg font-bold text-white">{previewPackage.title}</h3>
                <div className="flex items-center space-x-1 text-green-400 mt-1">
                  <DollarSign className="h-4 w-4" />
                  <span className="text-xl font-bold">${previewPackage.price}</span>
                </div>
              </div>
              <p className="text-slate-300 text-sm">{previewPackage.description}</p>
              <div className="flex flex-wrap gap-1">
                {previewPackage.facilities.map((f) => (
                  <Badge key={f} variant="secondary" className="text-xs bg-slate-700 text-slate-300">{f}</Badge>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Packages;
